import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';

interface ApiDebugComponentProps {
  title: string;
  apiCall: () => Promise<unknown>;
  buttonText?: string;
}

const ApiDebugComponent = ({
  title,
  apiCall,
  buttonText = 'Call API',
}: ApiDebugComponentProps) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const theme = useTheme();

  // Auto-resize textarea based on content (with scrolling enabled)
  useEffect(() => {
    if (textAreaRef.current && result) {
      const textarea = textAreaRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on content, with min constraint
      // Max height is handled by scrolling
      const newHeight = Math.max(200, textarea.scrollHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [result]);

  const handleApiCall = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiCall();
      // Format the response as JSON string with proper indentation
      const formattedResult = JSON.stringify(response, null, 2);
      setResult(formattedResult);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : String(err) || 'Unknown error occurred';
      setError(errorMessage);
      // Also show error in result box
      setResult(JSON.stringify({ error: errorMessage, details: err }, null, 2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: theme.palette.background.paper,
        maxHeight: '80vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Button
          variant="contained"
          onClick={handleApiCall}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Loading...
            </>
          ) : (
            buttonText
          )}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        value={result || ''}
        placeholder="API response will appear here..."
        variant="outlined"
        inputRef={textAreaRef}
        InputProps={{
          readOnly: true,
          sx: {
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            '& textarea': {
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              overflowY: 'auto',
              overflowX: 'auto',
            },
          },
        }}
        sx={{
          flexGrow: 1,
          '& .MuiOutlinedInput-root': {
            minHeight: '200px',
            alignItems: 'flex-start',
            '& textarea': {
              resize: 'vertical',
              minHeight: '200px',
              maxHeight: 'none',
              overflowY: 'auto',
              overflowX: 'auto',
            },
          },
        }}
      />
    </Paper>
  );
};

export default ApiDebugComponent;
